<template>
    <div class="cms-wrapper">
        <MultiSelect
            v-model="selected"
            :options="translatedOptions"
            optionLabel="label"
            dataKey="value"
            :class="classes"
            :disabled="disabled"
            :showToggleAll="false"
            :placeholder="placeholder"
            :name="name"
        >
            <template #value="slotProps">
                <span v-if="slotProps.value && slotProps.value.length === 1">
                    {{ slotProps.value[0].label }}
                </span>
                <span v-else-if="slotProps.value && slotProps.value.length > 1">
                    {{ slotProps.value[0].label }}, ...
                </span>
            </template>
        </MultiSelect>

        <CoreInputNotification
            v-if="isCoreInputNotificationShown"
            :validator="validator"
            :customNotificationMessage="customNotificationMessage"
        />
    </div>
</template>

<script>
import MultiSelect from 'primevue/multiselect'
import CoreInputNotification from './CoreInputNotification.vue'
import { sortOptionsByLabel } from '@/helpers/sortOptions'

export default {
    name: 'CoreMultiSelect',

    components: { MultiSelect, CoreInputNotification },

    props: {
        modelValue: {
            type: Array,
            default: () => [],
        },
        name: {
            type: String,
            default: '',
        },
        options: {
            required: true,
            type: Array,
        },
        condensed: {
            type: Boolean,
            default: false,
        },
        fullWidth: {
            type: Boolean,
            default: false,
        },
        validator: {
            type: Object,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: null,
        },
        customNotificationMessage: {
            type: Object,
            default: null,
        },
        sort: {
            type: Boolean,
            default: true,
        },
    },

    emits: ['update:modelValue'],

    mounted() {
        if (this.validator) {
            if (this.modelValue === null || this.modelValue.length === 0) {
                this.validator.$reset()
            } else {
                this.validator.$touch()
            }
        }
    },

    computed: {
        selected: {
            get() {
                if (!this.modelValue || !Array.isArray(this.modelValue)) {
                    return []
                }
                return this.translatedOptions.filter((option) => this.modelValue.includes(option.value))
            },
            set(newValue) {
                const selectedValues = newValue.map((obj) => obj.value)
                this.$emit('update:modelValue', selectedValues)

                if (this.validator) {
                    this.validator.$touch()
                }
            },
        },

        translatedOptions() {
            const translated = this.options.map((option) => ({
                ...option,
                label: this.$t(option.label),
            }))

            return this.sort ? sortOptionsByLabel(translated, this.$i18n?.locale) : translated
        },

        classes() {
            return {
                'core-multi-select': true,
                'core-multi-select--full-width': this.fullWidth,
                'core-multi-select--condensed': this.condensed,
                'core-multi-select--valid':
                    this.validator && !this.validator.$invalid && this.validator.$dirty,
                'core-multi-select--invalid':
                    this.validator && this.validator.$invalid && this.validator.$dirty,
                'core-multi-select--warning': this.isWarning,
            }
        },

        isWarning() {
            if (
                this.customNotificationMessage !== null &&
                this.customNotificationMessage.severity !== undefined &&
                this.customNotificationMessage.severity === 'warn'
            ) {
                return true
            }

            return false
        },

        validatorErrors() {
            if (this.validator.$error === undefined) {
                return {}
            }
            return this.validator.$error
        },

        validatorParams() {
            if (this.validator.$params === undefined) {
                return {}
            }
            return this.validator.$params
        },

        isCoreInputNotificationShown() {
            if (this.customNotificationMessage !== null) return true

            if (this.validator && this.validator.$dirty) return true

            return false
        },
    },
}
</script>

<style>
.cms-wrapper {
    width: 100%;
    position: relative;
}

.core-multi-select {
    width: 100%;

    & .p-multiselect-label.p-placeholder {
        font-size: var(--text-base);
    }

    &.p-multiselect.p-disabled {
        background-color: var(--color-disabled-bg);
        color: var(--text-disabled);
    }

    &.p-multiselect.core-multi-select--invalid.p-component,
    &.p-multiselect.core-multi-select--invalid.p-component:focus,
    &.p-multiselect.core-multi-select--invalid.p-component:hover {
        border: 1px solid var(--color-error);
    }

    &.p-multiselect.core-multi-select--warning.p-component,
    &.p-multiselect.core-multi-select--warning.p-component:focus,
    &.p-multiselect.core-multi-select--warning.p-component:hover {
        border: 1px solid var(--color-warning);
    }

    &.p-multiselect.core-multi-select--valid.p-component,
    &.p-multiselect.core-multi-select--valid.p-component:focus,
    &.p-multiselect.core-multi-select--valid.p-component:hover {
        border: 1px solid var(--color-primary);
    }
}
</style>
